/**
 * AME Patient Lift Configurator — guided quiz for patient lift / transfer aid selection
 * ES5 IIFE, no dependencies, deferred loading
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-pl-cfg]');
  if (!root) return;

  // --- DOM refs ---
  var ctaBtn = root.querySelector('[data-pl-cfg-cta]');
  var wizard = root.querySelector('#ame-pl-cfg-wizard');
  var resultsPanel = root.querySelector('[data-pl-cfg-results]');
  var resultsGrid = root.querySelector('[data-pl-cfg-results-grid]');
  var resultsHeading = root.querySelector('[data-pl-cfg-results-heading]');
  var resultsSubtitle = root.querySelector('[data-pl-cfg-results-subtitle]');
  var viewAllLink = root.querySelector('[data-pl-cfg-view-all]');
  var progressLabel = root.querySelector('[data-pl-cfg-progress-label]');
  var progressBar = root.querySelector('[data-pl-cfg-progressbar]');
  var progressFill = root.querySelector('[data-pl-cfg-progress-fill]');
  var liveRegion = root.querySelector('[data-pl-cfg-live]');

  var steps = root.querySelectorAll('[data-pl-cfg-step]');
  var TOTAL_STEPS = steps.length;

  // --- State ---
  var currentStep = 1;
  var answers = {};
  var products = [];

  // --- Parse product data ---
  var dataEl = document.getElementById('ame-pl-cfg-data');
  if (dataEl) {
    try {
      products = JSON.parse(dataEl.textContent);
    } catch (e) {
      products = [];
    }
  }

  // --- Session storage helpers ---
  function saveState() {
    try {
      sessionStorage.setItem('ame-pl-cfg-state', JSON.stringify({
        step: currentStep,
        answers: answers
      }));
    } catch (e) { /* ignore */ }
  }

  function loadState() {
    try {
      var raw = sessionStorage.getItem('ame-pl-cfg-state');
      if (raw) return JSON.parse(raw);
    } catch (e) { /* ignore */ }
    return null;
  }

  function clearState() {
    try { sessionStorage.removeItem('ame-pl-cfg-state'); } catch (e) { /* ignore */ }
  }

  // --- Announce to screen readers ---
  function announce(msg) {
    if (liveRegion) liveRegion.textContent = msg;
  }

  // --- Progress bar ---
  function updateProgress(step) {
    var pct = Math.round((step / TOTAL_STEPS) * 100);
    if (progressLabel) progressLabel.textContent = 'Step ' + step + ' of ' + TOTAL_STEPS;
    if (progressFill) progressFill.style.width = pct + '%';
    if (progressBar) {
      progressBar.setAttribute('aria-valuenow', pct);
    }
  }

  // --- Show/hide steps ---
  function showStep(n) {
    for (var i = 0; i < steps.length; i++) {
      steps[i].classList.toggle('is-active', i === n - 1);
    }
    currentStep = n;
    updateProgress(n);

    // Restore any previously selected answer for this step
    var stepEl = steps[n - 1];
    var radios = stepEl.querySelectorAll('input[type="radio"]');
    var fieldName = radios.length > 0 ? radios[0].name : '';
    if (fieldName && answers[fieldName]) {
      for (var j = 0; j < radios.length; j++) {
        radios[j].checked = radios[j].value === answers[fieldName];
      }
    }

    updateNextBtn(stepEl);

    // Focus management
    var question = stepEl.querySelector('.ame-pl-cfg__question');
    if (question) question.focus();

    announce('Step ' + n + ' of ' + TOTAL_STEPS);
    saveState();
  }

  // --- Next button enabled/disabled ---
  function updateNextBtn(stepEl) {
    var nextBtn = stepEl.querySelector('[data-pl-cfg-next]');
    var checked = stepEl.querySelector('input[type="radio"]:checked');
    if (nextBtn) nextBtn.disabled = !checked;
  }

  // --- Open wizard ---
  function openWizard() {
    wizard.hidden = false;
    ctaBtn.setAttribute('aria-expanded', 'true');
    showStep(currentStep);
    requestAnimationFrame(function () {
      var rect = wizard.getBoundingClientRect();
      var headerEl = document.querySelector('#shopify-section-header') || document.querySelector('.section-header');
      var headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
      window.scrollTo({ top: window.scrollY + rect.top - headerH - 16, behavior: 'smooth' });
    });
  }

  // --- Close wizard ---
  function closeWizard() {
    wizard.hidden = true;
    resultsPanel.classList.remove('is-active');
    ctaBtn.setAttribute('aria-expanded', 'false');
    ctaBtn.focus();
    currentStep = 1;
    answers = {};
    clearState();
  }

  // --- Collect answer from current step ---
  function collectAnswer() {
    var stepEl = steps[currentStep - 1];
    var checked = stepEl.querySelector('input[type="radio"]:checked');
    if (checked) {
      answers[checked.name] = checked.value;
    }
  }

  // --- Tag helper ---
  function hasTag(tags, tag) {
    if (!tags || !tags.length) return false;
    for (var i = 0; i < tags.length; i++) {
      if (tags[i] === tag) return true;
    }
    return false;
  }

  // --- Scoring: Step 1 — Transfer Type (weight: 4) ---
  function scoreTransfer(product, transfer) {
    var tags = product.tags || [];
    var score = 0;

    switch (transfer) {
      case 'full_body':
        if (hasTag(tags, 'patient-lift') || hasTag(tags, 'floor-lift')) score += 10;
        if (hasTag(tags, 'full-body')) score += 4;
        if (hasTag(tags, 'sit-to-stand') && !hasTag(tags, 'patient-lift')) score -= 3;
        if (hasTag(tags, 'sling')) score += 2; // slings are relevant
        break;
      case 'sit_to_stand':
        if (hasTag(tags, 'sit-to-stand') || hasTag(tags, 'stand-assist')) score += 10;
        if (hasTag(tags, 'patient-lift') && !hasTag(tags, 'sit-to-stand')) score -= 2;
        break;
      case 'toileting':
        if (hasTag(tags, 'toileting')) score += 10;
        if (hasTag(tags, 'patient-lift') || hasTag(tags, 'floor-lift')) score += 4;
        if (hasTag(tags, 'sling') && hasTag(tags, 'toileting')) score += 4;
        break;
      case 'car_transfer':
        if (hasTag(tags, 'car-transfer')) score += 10;
        if (hasTag(tags, 'portable')) score += 4;
        if (hasTag(tags, 'compact')) score += 3;
        if (!hasTag(tags, 'car-transfer') && !hasTag(tags, 'portable')) score -= 3;
        break;
    }
    return score;
  }

  // --- Scoring: Step 2 — Weight Capacity (weight: 4, hard constraint) ---
  function scoreCapacity(product, minCapacity) {
    if (product.weight_capacity === null) return 0;
    if (product.weight_capacity < minCapacity) return -100;
    // Bonus for exact fit (not oversized)
    if (product.weight_capacity <= minCapacity * 1.5) return 10;
    return 6;
  }

  // --- Scoring: Step 3 — Power Preference (weight: 2) ---
  function scorePower(product, power) {
    var tags = product.tags || [];
    var score = 0;

    switch (power) {
      case 'electric':
        if (hasTag(tags, 'electric')) score += 8;
        if (hasTag(tags, 'battery') || hasTag(tags, 'rechargeable')) score += 3;
        if (hasTag(tags, 'hydraulic') && !hasTag(tags, 'electric')) score -= 4;
        break;
      case 'hydraulic':
        if (hasTag(tags, 'hydraulic')) score += 8;
        if (hasTag(tags, 'manual')) score += 3;
        if (hasTag(tags, 'electric') && !hasTag(tags, 'hydraulic')) score -= 4;
        break;
      case 'no_pref':
        score += 5;
        break;
    }
    return score;
  }

  // --- Scoring: Step 4 — Slings filter (weight: 1) ---
  function scoreSlings(product, includeSlings) {
    var tags = product.tags || [];
    var isSling = hasTag(tags, 'sling') || hasTag(tags, 'patient-sling');

    if (includeSlings === 'no' && isSling) {
      return -100; // Exclude slings entirely
    }
    if (includeSlings === 'yes' && isSling) {
      return 4; // Boost slings slightly
    }
    return 0;
  }

  // --- Master scoring ---
  function scoreProducts(ans) {
    var transfer = ans.pl_transfer || 'full_body';
    var minCapacity = parseInt(ans.pl_weight, 10) || 200;
    var power = ans.pl_power || 'no_pref';
    var slings = ans.pl_slings || 'no';

    var scored = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];

      var tScore = scoreTransfer(p, transfer);
      var cScore = scoreCapacity(p, minCapacity);
      var pScore = scorePower(p, power);
      var sScore = scoreSlings(p, slings);

      // Weighted total: transfer(4) + capacity(4) + power(2) + slings(1)
      var total = (tScore * 4) + (cScore * 4) + (pScore * 2) + (sScore * 1);

      scored.push({
        product: p,
        total: total,
        transferScore: tScore,
        capacityScore: cScore,
        powerScore: pScore,
        slingsScore: sScore
      });
    }

    scored.sort(function (a, b) { return b.total - a.total; });
    return scored;
  }

  // --- Match badge label ---
  function getMatchLabel(index) {
    switch (index) {
      case 0: return 'Best Match';
      case 1: return 'Great Fit';
      case 2: return 'Strong Pick';
      default: return 'Good Option';
    }
  }

  // --- Format price ---
  function formatPrice(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // --- Build spec pills ---
  function buildSpecs(product) {
    var tags = product.tags || [];
    var specs = [];
    if (product.weight_capacity) specs.push(product.weight_capacity + ' lb capacity');
    if (hasTag(tags, 'electric')) specs.push('Electric');
    else if (hasTag(tags, 'hydraulic')) specs.push('Hydraulic');
    if (product.product_weight) specs.push(product.product_weight + ' lbs');
    if (hasTag(tags, 'sling')) specs.push('Sling');
    if (hasTag(tags, 'sit-to-stand') || hasTag(tags, 'stand-assist')) specs.push('Sit-to-Stand');
    if (hasTag(tags, 'full-body')) specs.push('Full Body');
    if (hasTag(tags, 'portable')) specs.push('Portable');
    return specs.slice(0, 4);
  }

  // --- HTML escaping ---
  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // --- Render result cards ---
  function renderResults(scored) {
    var top = [];
    for (var i = 0; i < scored.length && top.length < 5; i++) {
      if (scored[i].total > 0) top.push(scored[i]);
    }

    // Fallback: if no products above threshold, show top 3
    if (top.length === 0 && scored.length > 0) {
      for (var f = 0; f < scored.length && top.length < 3; f++) {
        top.push(scored[f]);
      }
    }

    resultsGrid.innerHTML = '';

    if (top.length === 0) {
      resultsGrid.innerHTML =
        '<div class="ame-pl-cfg__no-match">' +
          '<p>No exact matches found for your criteria.</p>' +
          '<p>Try adjusting your preferences or <a href="tel:+18039261493">call (803) 926-1493</a> for expert help.</p>' +
        '</div>';
      resultsSubtitle.textContent = '';
      announce('No exact matches found. Try adjusting your preferences or call for help.');
      return top;
    }

    if (top[0].total <= 0) {
      resultsSubtitle.textContent =
        'We couldn\'t find an exact match, but here are our closest options. ' +
        'Call (803) 926-1493 for personalized help.';
    } else {
      resultsSubtitle.textContent = 'Based on your answers, here are our top ' + top.length + ' recommendations.';
    }

    for (var j = 0; j < top.length; j++) {
      var p = top[j].product;
      var specs = buildSpecs(p);
      var specsHtml = '';
      for (var k = 0; k < specs.length; k++) {
        specsHtml += '<span class="ame-pl-cfg__card-spec">' + escapeHtml(specs[k]) + '</span>';
      }

      var priceHtml = '';
      if (p.call_for_price) {
        priceHtml = '<span style="color:var(--ame-primary)">Call for Price</span>';
      } else {
        priceHtml = formatPrice(p.price);
        if (p.compare_at_price && p.compare_at_price > p.price) {
          priceHtml += ' <span class="ame-card__compare-price">' + formatPrice(p.compare_at_price) + '</span>';
        }
      }

      var vendorHtml = p.vendor ? '<span class="ame-card__vendor">' + escapeHtml(p.vendor) + '</span>' : '';

      var card =
        '<a href="' + escapeHtml(p.url) + '" class="ame-card">' +
          '<div class="ame-card__image-wrap">' +
            '<span class="ame-pl-cfg__match-badge">' + getMatchLabel(j) + '</span>' +
            (p.image ? '<img class="ame-card__image" src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="500" height="500">' : '') +
          '</div>' +
          '<div class="ame-card__body">' +
            vendorHtml +
            '<h3 class="ame-card__title">' + escapeHtml(p.title) + '</h3>' +
            '<div class="ame-pl-cfg__card-specs">' + specsHtml + '</div>' +
            '<div class="ame-card__price">' + priceHtml + '</div>' +
          '</div>' +
        '</a>';

      resultsGrid.insertAdjacentHTML('beforeend', card);
    }

    announce('Found ' + top.length + ' matching patient lifts. Showing your top recommendations.');
    return top;
  }

  // --- Build filter URL for "View All" ---
  function buildFilterUrl(ans) {
    var base = '/collections/patient-lifts';
    var params = [];

    // Transfer type tag filter
    if (ans.pl_transfer) {
      switch (ans.pl_transfer) {
        case 'full_body':
          params.push('filter.p.tag=patient-lift');
          break;
        case 'sit_to_stand':
          params.push('filter.p.tag=sit-to-stand');
          break;
        case 'toileting':
          params.push('filter.p.tag=toileting');
          break;
        case 'car_transfer':
          params.push('filter.p.tag=car-transfer');
          break;
      }
    }

    return params.length > 0 ? base + '?' + params.join('&') : base;
  }

  // --- Show results ---
  function showResults() {
    wizard.hidden = true;
    var scored = scoreProducts(answers);
    renderResults(scored);

    if (viewAllLink) viewAllLink.href = buildFilterUrl(answers);

    resultsPanel.classList.add('is-active');
    if (resultsHeading) resultsHeading.focus();

    clearState();
  }

  // --- Event: CTA click ---
  ctaBtn.addEventListener('click', function () {
    if (wizard.hidden && !resultsPanel.classList.contains('is-active')) {
      openWizard();
    } else {
      closeWizard();
    }
  });

  // --- Event: radio change ---
  root.addEventListener('change', function (e) {
    if (e.target.type === 'radio' && e.target.closest('[data-pl-cfg-step]')) {
      var stepEl = e.target.closest('[data-pl-cfg-step]');
      updateNextBtn(stepEl);
    }
  });

  // --- Event: next ---
  root.addEventListener('click', function (e) {
    var nextBtn = e.target.closest('[data-pl-cfg-next]');
    if (!nextBtn || nextBtn.disabled) return;

    collectAnswer();

    if (currentStep < TOTAL_STEPS) {
      showStep(currentStep + 1);
    } else {
      showResults();
    }
  });

  // --- Event: back ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-pl-cfg-back]')) {
      collectAnswer();
      if (currentStep > 1) {
        showStep(currentStep - 1);
      }
    }
  });

  // --- Event: close ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-pl-cfg-close]')) {
      closeWizard();
    }
  });

  // --- Event: restart ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-pl-cfg-restart]')) {
      resultsPanel.classList.remove('is-active');
      answers = {};
      currentStep = 1;

      var radios = root.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        radios[i].checked = false;
      }

      clearState();
      openWizard();
    }
  });

  // --- Event: Banner CTA (button in ame-collection-banner) ---
  var bannerCta = document.querySelector('[data-pl-cfg-banner-cta]');
  if (bannerCta) {
    bannerCta.addEventListener('click', function () {
      openWizard();
    });
  }

  // --- Event: Escape key ---
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.key === 'Esc') && !wizard.hidden) {
      closeWizard();
    }
  });

  // --- Restore session ---
  var saved = loadState();
  if (saved && saved.step && saved.answers) {
    answers = saved.answers;
    currentStep = saved.step;

    var fieldNames = Object.keys(answers);
    for (var f = 0; f < fieldNames.length; f++) {
      var radio = root.querySelector('input[name="' + fieldNames[f] + '"][value="' + answers[fieldNames[f]] + '"]');
      if (radio) radio.checked = true;
    }

    openWizard();
  }
})();
